import {HttpClient} from "@angular/common/http";
import {Injectable} from "@angular/core";
import {
  ActionResult,
  EventItem,
  EventStatus,
  League,
  LeagueFilter,
  Lookups,
  PagedFilter,
  ValidatorResult
} from "@model";
import {BehaviorSubject, finalize, forkJoin, Observable, of} from "rxjs";
import {catchError, map, tap} from "rxjs/operators";
import {LoaderService} from "./loader.service";
import {EnvironmentService} from "./environment.service";
import {LeagueScoringService} from "./league-scoring.service";

@Injectable({ providedIn: 'root' })
export class LeagueService {
  private _currentLeague = new BehaviorSubject<League | undefined>(undefined);

  get currentLeague(): BehaviorSubject<League | undefined> {
    return this._currentLeague;
  }

  constructor(
    private http: HttpClient,
    private loaderService: LoaderService,
    private environmentService: EnvironmentService,
    private leagueScoringService: LeagueScoringService
  ) {}

  setCurrentLeague(league: League | undefined): void {
    this.currentLeague.next(league);
  }

  getFilteredLeagueStatuses(): Observable<Lookups[] | []> {
    return this.http.get<ActionResult<Lookups[]>>(`${this.environmentService.getApiUrl()}/api/lookups/league/status`).pipe(
      map(value => value?.result?.length ? value.result : [])
    );
  }

  findLeagues(filter: PagedFilter<LeagueFilter>): Observable<League[]> {
    return this.http.post<ActionResult<League[]>>(`${this.environmentService.getApiUrl()}/api/leagues`, filter).pipe(
      map(actionResult => actionResult.resultCode !== -1 && actionResult.result ? actionResult.result : [])
    );
  }

  findPublicLeagues(filter: PagedFilter<LeagueFilter>): Observable<League[]> {
    return this.http.post<ActionResult<League[]>>(`${this.environmentService.getApiUrl()}/api/public/leagues`, filter).pipe(
      map(actionResult => actionResult.resultCode !== -1 && actionResult.result ? actionResult.result : [])
    );
  }

  fetchLeague(leagueId: number): Observable<League | undefined> {
    return this.http.post<ActionResult<League>>(`${this.environmentService.getApiUrl()}/api/leagues/fetchbyid`, leagueId).pipe(
      map(actionResult => {
        const league = actionResult.resultCode !== -1 && actionResult.result ? actionResult.result : undefined;
        this.currentLeague.next(league);
        return league;
      })
    );
  }

  findLeagueByIdProtected(
      leagueId: number,
      loading: boolean = false
  ): Observable<League | undefined> {

    if (loading) this.loaderService.startLoading();

    return forkJoin([
      this.fetchLeague(leagueId).pipe(catchError(() => of(undefined))),
      this.leagueScoringService.getScoringCategorizationsByLeagueId(leagueId).pipe(catchError(() => of([]))),
      this.leagueScoringService.getIndividualScoringConfigurationsByLeagueId(leagueId).pipe(catchError(() => of([]))),
      this.leagueScoringService.getGroupScoringConfigurationsByLeagueId(leagueId).pipe(catchError(() => of([]))),
      this.leagueScoringService.getScoringDefinitionsByLeagueId(leagueId).pipe(catchError(() => of([]))),
    ]).pipe(
        map(([league, scoringCategorizations, individualScoringConfigurations, groupScoringConfigurations, scoringDefinitions]) =>
            this.combineLeagueData({ league, scoringCategorizations, individualScoringConfigurations, groupScoringConfigurations, scoringDefinitions })
        ),
        finalize(() => {
          if (loading) this.loaderService.dismissLoading();
        })
    );
  }


  private combineLeagueData(data: any): League | undefined {
    if (!data?.league) return undefined;
    return {
      ...data.league,
      scoringCategorizations: data.scoringCategorizations ?? [],
      individualScoringConfigurations: data.individualScoringConfigurations ?? [],
      groupScoringConfigurations: data.groupScoringConfigurations ?? [],
      scoringDefinitions: data.scoringDefinitions ?? [],
    };
  }


  fetchLeaguePublic(leagueId: number): Observable<League | undefined> {
    return this.http.post<ActionResult<League>>(`${this.environmentService.getApiUrl()}/api/public/leagues/fetchbyid`, leagueId).pipe(
      map(actionResult => {
        const league = actionResult.resultCode !== -1 && actionResult.result ? actionResult.result : undefined;
        this.currentLeague.next(league);
        return league;
      })
    );
  }


  updateLeague(league: League, onSuccess: (league: League) => void, onError: () => void): void {
    this.http.post<ActionResult<boolean | number>>(
      `${this.environmentService.getApiUrl()}${league.id ? "/api/leagues/update" : "/api/leagues/add"}`, league
    ).subscribe(value => {
      if (value?.result) {
        if (typeof value.result === "boolean") {
          this.setCurrentLeague(league);
        } else {
          league.id = value.result;
          this.setCurrentLeague(league);
        }
        onSuccess(league);
      } else {
        onError();
      }
    });
  }

  copyLeague(leagueId: number, league: League, onSuccess: (league: League) => void, onError: () => void): void {
    this.http.post<ActionResult<League>>(
        `${this.environmentService.getApiUrl()}/api/leagues/copy?leagueId=${leagueId}`, league)
        .pipe(finalize(() => this.loaderService.dismissLoading()))
        .subscribe(actionResult => {
          actionResult?.result ? onSuccess?.(actionResult.result) : onError?.();
        });
  }

  deleteLeague(league: League, onSuccess: () => void, onError: () => void): void {
    this.http.post<ActionResult<boolean>>(
      `${this.environmentService.getApiUrl()}/api/leagues/delete`, league.id
    ).subscribe(actionResult => {
      actionResult.success ? onSuccess() : onError();
    });
  }

  hideLeague(league: League, onSuccess?: () => void, onError?: () => void) {
    this.loaderService.startLoading();
    return this.http.post<ActionResult<Boolean>>(
        `${this.environmentService.getApiUrl()}/api/leagues/hide`, league.id!
    ).pipe(
        tap(() => this.loaderService.dismissLoading())
    ).subscribe(actionResult => {
      if (actionResult.success) {
        if (onSuccess) {
          onSuccess();
        }
      }
      if (onError && !actionResult.success) {
        onError();
      }
    });
  }

  validateLeague(leagueId: number, hideLoading: boolean = false): Observable<ValidatorResult | null> {
    if (!hideLoading) {
      this.loaderService.startLoading();
    }
    return this.http.post<ActionResult<ValidatorResult>>(
        `${this.environmentService.getApiUrl()}/api/leagues/validate`, leagueId!
    ).pipe(
        tap(() => this.loaderService.dismissLoading()),
        map(value => {
          const result = value?.result ? ValidatorResult.deserialize(value.result) : null;
          if (result?.hasErrors()) return result;
          return null;
        })
    )
  }

  publishLeague(league: League, onSuccess: () => void, onError: () => void): void {
    this.http.post<ActionResult<boolean>>(this.environmentService.getApiUrl() + "/api/leagues/publish", league.id)
        .subscribe((actionResult: ActionResult<boolean>) => {
          if (actionResult.resultCode !== -1) {
            onSuccess();
          } else {
            onError();
          }
        });
  }
}
